import { UserSelect } from "@app/filter/UserSelect";
import { Button } from "@comps/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import User, { EditRightAccess, FullRightAccess, implicitOptionsForLevel, NoneRightAccess, ReadRightAccess, Right, RightAccess, RightLevel, Role, ViewRightType } from "@dto/Users";
import { ShareViewRequest, View } from "@dto/views";
import { BASE_URL } from "@utils/constants";
import { fetchRoles } from "@utils/model";
import { toastError } from "@utils/toasts";
import { TW_ViewSharingEditor, TW_ViewSharingEditorNewFilter, TW_ViewSharingEditorNoRoles, TW_ViewSharingEditorOwnerButton, TW_ViewSharingEditorOwnerSection, TW_ViewSharingEditorOwnerSectionButton, TW_ViewSharingEditorOwnerSectionTitle, TW_ViewSharingEditorRemoveFilter, TW_ViewSharingEditorRemoveFilterDisabled, TW_ViewSharingEditorRoleRightSelect, TW_ViewSharingEditorRoleSelect, TW_ViewSharingEditorSection, TW_ViewSharingEditorSectionTitle, TW_ViewSharingEditorUserRightSelect, getWsButtonStyle, useHover, useIconHover } from "@utils/twClasses";
import { put } from "@utils/utils";
import { indexViewRoleSharing, withoutViewRight } from "@utils/viewSharing";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useAppStore, useModelStore, useUserStore } from "src/store";


interface ViewSharingEditorProps {
    view: View,
    onClose: () => void,
    onViewUpdate: (view: View) => void,
    onChangeOwner?: () => void
}

function ViewSharingEditor({ view, onClose, onViewUpdate, onChangeOwner }: ViewSharingEditorProps) {

    const [roles, setRoles] = useState<Role[]>([]);
    const [usedRoleKeys, setUsedRoleKeys] = useState<string[]>([]);
    const [rightPerRole, setRightPerRole] = useState<Map<string, Right | null>>(new Map());
    const workspace = useModelStore((state) => state.selectedWorkspace);
    const object = useModelStore((state) => state.selectedObjectInfos?.object);
    const users = useAppStore((state) => state.usersByKeys);
    const fetchUsers = useAppStore((state) => state.fetchUsers);
    const fetchViewInfos = useModelStore((state) => state.fetchViewInfos);
    const currentUser = useUserStore((state) => state.user);
    const { t } = useTranslation();
    const [userSharesByType, setUserSharesByType] = useState<Map<RightAccess, User[]>>(new Map());
    const [usedUserRightAccess, setUsedUserRightAccess] = useState<RightAccess[]>([]);
    const wsColor = useModelStore((state) => state.wsColor);
    const wsDarkColor = useModelStore((state) => state.wsDarkColor);
    const ownerButton = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

    useEffect(() => {
        const userSharesByType = new Map<RightAccess, User[]>();
        Array.from(users.values()).forEach((user) => {
            const rights = (user.rights ?? []).filter((right) => right.type === ViewRightType && right.recordKey === view._key);
            rights.forEach((right) => {
                userSharesByType.set(right.access, [...(userSharesByType.get(right.access) ?? []), user]);
            });
        });
        const usedRights = Array.from(userSharesByType.keys());
        setUsedUserRightAccess(viewRightAccesses.filter(right => usedRights.includes(right)));
        setUserSharesByType(userSharesByType);
    }, [view, users]);

    function onRoleFetched(fetchedRoles: Role[]) {
        // Always rebuild the index from the fetched roles: the previous state
        // must never be mutated, otherwise every refetch (the view is reloaded
        // after each share) would pile up duplicated keys and eventually hide
        // the empty "new share" row (usedRoleKeys.length >= roles.length).
        const { rightPerRole, usedRoleKeys } = indexViewRoleSharing(fetchedRoles, view._key);
        setRightPerRole(rightPerRole);
        setRoles(fetchedRoles);
        setUsedRoleKeys(usedRoleKeys);
    }

    useEffect(() => {
        fetchRoles(workspace?._key, null, null, onRoleFetched);
    }, [workspace, view]);

    const onRoleChange = (roleKey: string, newRole: Role) => {

        const newRight = newRole.rights.find((right) => right.type === ViewRightType && right.recordKey == view._key)

        const shareViewRequest: ShareViewRequest = {
            viewKey: view._key,
            roleKey: roleKey,
            right: newRight ?? null
        };

        put<Role>(`${BASE_URL}/model/workspaces/${workspace?._key}/objects/${object?._key}/views/${view._key}/share`, shareViewRequest).then((response) => {
            if (isCompozzError(response)) {
                const cError = response as CompozzError
                if (cError.i18n) {
                    toastError(translate(t, cError.i18n, cError.params));
                } else {
                    toastError(cError.message);
                }
            } else {
                const role = response as Role;
                const newRoles = roles.map((r) => {
                    if (r._key === roleKey) {
                        return role;
                    }
                    return r;
                });
                const newRightPerRole = new Map<string, Right | null>(rightPerRole);
                const newViewRight = newRole.rights.find((r) => r.type === ViewRightType && r.recordKey === view._key) ?? null;
                if (newViewRight) {
                    newRightPerRole.set(role._key, newViewRight);
                } else {
                    newRightPerRole.delete(roleKey);
                }
                let newUsedRoleKeys = [...usedRoleKeys];
                if (usedRoleKeys.includes(roleKey) && !newViewRight) {
                    newUsedRoleKeys = newUsedRoleKeys.filter((key) => key !== roleKey);
                } else if (!usedRoleKeys.includes(roleKey) && newViewRight) {
                    newUsedRoleKeys.push(roleKey);
                }
                let isShared = false;
                const newRightValues = Array.from(newRightPerRole.values());
                for (const right of newRightValues) {
                    if (right && right.active && right.access !== "NONE") {
                        isShared = true;
                        break;
                    }
                }
                const newView = {
                    ...view,
                    shared: isShared
                };
                onViewUpdate(newView);

                setRoles(newRoles);
                setRightPerRole(newRightPerRole);
                setUsedRoleKeys(newUsedRoleKeys);
                fetchViewInfos(workspace!._key!, object!._key!, view._key, onViewUpdate);
            }
        })
    }

    const onUserChange = (rightAccess: RightAccess, oldUsers: User[], newUsers: User[]) => {

        const oldUsersKeys = oldUsers.map((user) => user._key);
        const newUsersKeys = newUsers.map((user) => user._key);

        const usersToAdd = newUsers.filter((user) => !oldUsersKeys.includes(user._key));
        const usersToRemove = oldUsers.filter((user) => !newUsersKeys.includes(user._key));

        Promise.all(usersToAdd.map(async (user) => {
            // The sharing offers levels only: EDIT and FULL carry the add and
            // delete options, as they implicitly did before the level + options split.
            const newRight: Right = {
                type: ViewRightType,
                access: rightAccess,
                options: implicitOptionsForLevel(rightAccess),
                recordKey: view._key,
                active: true
            };
            updateUserRights(user, newRight, () => {
                fetchUsers();
                fetchViewInfos(workspace!._key!, object!._key!, view._key, onViewUpdate);
            });
        }))

        Promise.all(usersToRemove.map(async (user) => {
            const newRight: Right = {
                type: ViewRightType,
                access: NoneRightAccess,
                options: [],
                recordKey: view._key,
                active: true
            };
            updateUserRights(user, newRight, () => {
                fetchUsers();
                fetchViewInfos(workspace!._key!, object!._key!, view._key, onViewUpdate);
            });
        }))
    }

    const onRemoveRightAccess = (_rightAccess: RightAccess, oldUsers: User[]) => {

        oldUsers.map((user) => {
            const newRight: Right = {
                type: ViewRightType,
                access: NoneRightAccess,
                options: [],
                recordKey: view._key,
                active: true
            };
            updateUserRights(user, newRight, () => {
                fetchUsers();
                fetchViewInfos(workspace!._key!, object!._key!, view._key, onViewUpdate);
            });
        });
    }


    const updateUserRights = (user: User, newRight: Right, onUpdate: () => void) => {

        const shareViewRequest: ShareViewRequest = {
            viewKey: view._key,
            userKey: user._key,
            right: newRight ?? null
        };

        put<Role>(`${BASE_URL}/model/workspaces/${workspace?._key}/objects/${object?._key}/views/${view._key}/share`, shareViewRequest).then((response) => {
            if (isCompozzError(response)) {
                const cError = response as CompozzError
                if (cError.i18n) {
                    toastError(translate(t, cError.i18n, cError.params));
                } else {
                    toastError(cError.message);
                }
            } else {
                fetchUsers();
                fetchViewInfos(workspace!._key!, object!._key!, view._key, onViewUpdate);
            }
        })
    }

    const roleItemKeyPref = view._key + "-sharing-role-"
    const userItemKeyPref = view._key + "-sharing-user-"
    const ownerUser = users.get(view.ownerKey ?? "");
    const canChangeOwnership =  currentUser?.owner || view.ownerKey != null && view.ownerKey === currentUser?._key;
    return (
        <div className={TW_ViewSharingEditor}>
            {ownerUser && <div className={TW_ViewSharingEditorOwnerSection}>
                <p className={TW_ViewSharingEditorOwnerSectionTitle}>{translate(t, "view.sharing.owner", [ownerUser.firstname, ownerUser.lastname, ownerUser.username ?? ""])}</p>
                {canChangeOwnership && onChangeOwner && (
                    <div className={TW_ViewSharingEditorOwnerSectionButton}>
                        <Button className={TW_ViewSharingEditorOwnerButton} onClick={onChangeOwner} {...ownerButton}>{t("view.sharing.change.owner.button")}</Button>
                    </div>
                )}
            </div>}
            <div className={TW_ViewSharingEditorSection}>
                <p className={TW_ViewSharingEditorSectionTitle}>{t("view.sharing.roles")}</p>
                {roles.length == 0 && <p className={TW_ViewSharingEditorNoRoles}>{t("view.sharing.no.defined.roles")}</p>}
                {roles.length > 0 &&
                    roles.filter(role => rightPerRole.get(role._key)).map((role, index) => {
                        return <RoleViewSharingItem key={roleItemKeyPref + index} view={view} role={role} right={rightPerRole.get(role._key) ?? null} onRoleChange={onRoleChange} roles={roles} usedRoleKeys={usedRoleKeys} />
                    })}
                {usedRoleKeys.length < roles.length && <RoleViewSharingItem key={roleItemKeyPref + usedRoleKeys.length} view={view} role={null} right={null} onRoleChange={onRoleChange} roles={roles} usedRoleKeys={usedRoleKeys} />}
            </div>
            <div className={TW_ViewSharingEditorSection}>
                <p className={TW_ViewSharingEditorSectionTitle}>{t("view.sharing.users")}</p>
                {usedUserRightAccess.filter(right => userSharesByType.has(right) && userSharesByType.get(right)!.length > 0).map((right, index) => {
                    const users = userSharesByType.get(right);
                    return <UserViewSharingItem key={userItemKeyPref + "-" + usedUserRightAccess.length + right + "-" + index} right={right} users={users!} onUserChange={onUserChange} onRemoveRight={onRemoveRightAccess} usedRightAccess={usedUserRightAccess} />
                })}
                {usedUserRightAccess.length < viewRightAccesses.length && <UserViewSharingItem key={userItemKeyPref + usedUserRightAccess.length} right={null} users={[]} onUserChange={onUserChange} onRemoveRight={onRemoveRightAccess} usedRightAccess={usedUserRightAccess} />}
            </div>
        </div>
    )
}

interface RoleViewSharingItemProps {
    view: View,
    role: Role | null,
    right: Right | null,
    onRoleChange: (roleKey: string, newRole: Role) => void,
    roles: Role[],
    usedRoleKeys: string[]
}

// Levels offered by the view sharing (no options menu here: EDIT and FULL carry add + delete)
const viewRightAccesses: RightLevel[] = [FullRightAccess, EditRightAccess, ReadRightAccess, NoneRightAccess]

function RoleViewSharingItem({ view, role, right, onRoleChange, roles, usedRoleKeys }: RoleViewSharingItemProps) {

    const { t } = useTranslation();
    const [selectedRoleKey, setSelectedRoleKey] = useState<string | undefined>(undefined);
    const wsColor = useModelStore((state) => state.wsColor);
    const trashHover = useIconHover("#00000088", wsColor);

    useEffect(() => {
        if (role) {
            setSelectedRoleKey(role._key);
        }
    }, [role, right]);

    const onRoleSelectionChange = (roleKey: string) => {
        if (roleKey !== selectedRoleKey) {
            setSelectedRoleKey(roleKey);
        }
    }

    const onRightAccessChange = (newRightAccess: string) => {
        if (!selectedRoleKey) {
            return;
        }
        let foundRole = roles.find(r => r._key === selectedRoleKey);
        if (!foundRole) {
            return;
        }
        if (right?.access !== newRightAccess) {
            if (!newRightAccess) {
                const newRights = withoutViewRight(foundRole.rights, view._key);
                const newRole = {
                    ...foundRole,
                    rights: newRights
                };
                onRoleChange(foundRole._key, newRole);
            } else {
                const newRight: Right = (right ? {
                    ...right,
                    access: newRightAccess as RightAccess,
                    options: implicitOptionsForLevel(newRightAccess as RightAccess)
                } : {
                    type: ViewRightType,
                    access: newRightAccess as RightAccess,
                    options: implicitOptionsForLevel(newRightAccess as RightAccess),
                    recordKey: view._key,
                    active: true
                });
                const newRights = withoutViewRight(foundRole.rights, view._key);
                newRights.push(newRight);
                const newRole = {
                    ...foundRole,
                    rights: newRights
                };
                onRoleChange(foundRole._key, newRole);
            }
        }
    }

    const onRemoveRight = () => {
        if (!selectedRoleKey) {
            return;
        }
        const roleToRemove = roles.find(r => r._key === selectedRoleKey);
        if (!roleToRemove) {
            return;
        }
        const newRights = withoutViewRight(roleToRemove.rights, view._key);
        const newRole = {
            ...roleToRemove,
            rights: newRights
        };
        setSelectedRoleKey(undefined);
        onRoleChange(roleToRemove._key, newRole);
    }

    const disabledTrash = !selectedRoleKey;
    return (
        <div className={TW_ViewSharingEditorNewFilter}>
            <Select key={selectedRoleKey ?? "undefined"} onValueChange={onRoleSelectionChange} value={selectedRoleKey} disabled={selectedRoleKey !== undefined}>
                <SelectTrigger className={TW_ViewSharingEditorRoleSelect}>
                    <SelectValue placeholder={t("view.sharing.role.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                    {roles &&
                        roles.map((role) => {
                            const disabled = usedRoleKeys.includes(role._key);
                            return (
                                <SelectItem key={role._key} value={role._key} disabled={disabled}>{role.name}</SelectItem>
                            )
                        })}
                </SelectContent>
            </Select>
            <Select onValueChange={onRightAccessChange} value={right?.access}>
                <SelectTrigger className={TW_ViewSharingEditorRoleRightSelect}>
                    <SelectValue placeholder={t("view.sharing.right.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                    {
                        viewRightAccesses.map((right) => {
                            return (
                                <SelectItem key={right} value={right}>{t(`right.access.value.view.${right}`)}</SelectItem>
                            )
                        })}
                </SelectContent>
            </Select>
            {disabledTrash && <Trash2 className={TW_ViewSharingEditorRemoveFilterDisabled} />}
            {!disabledTrash && <Trash2 className={TW_ViewSharingEditorRemoveFilter} onClick={onRemoveRight} {...trashHover} />}
        </div>
    )
}

interface UserViewSharingItemProps {
    right: RightAccess | null,
    users: User[],
    onUserChange: (rightAccess: RightAccess, oldUsers: User[], newUsers: User[]) => void,
    onRemoveRight: (rightAccess: RightAccess, oldUsers: User[]) => void,
    usedRightAccess: RightAccess[]
}

function UserViewSharingItem({ right, users, onUserChange, onRemoveRight, usedRightAccess }: UserViewSharingItemProps) {

    const { t } = useTranslation();
    const wsColor = useModelStore((state) => state.wsColor);
    const trashHover = useIconHover("#00000088", wsColor);
    const allUsers = Array.from(useAppStore((state) => state.usersByKeys).values());
    const [selectedRightAccess, setSelectedRightAccess] = useState<RightAccess | undefined>(right ?? undefined);
    const [selectedUsers, setSelectedUsers] = useState<User[]>(users);

    const onRightAccessSelectionChange = (newRightAccess: string) => {
        if (newRightAccess !== selectedRightAccess) {
            setSelectedRightAccess(newRightAccess as RightAccess);
        }
    }

    const onUserSelectionChange = (selectedUsers: User[], _currentUserSelected: boolean) => {
        if (!selectedRightAccess) {
            return;
        }
        setSelectedUsers(selectedUsers);
        onUserChange(selectedRightAccess, users, selectedUsers);

    }

    const onRemoveRightAccess = () => {
        if (!selectedRightAccess) {
            return;
        }
        setSelectedRightAccess(undefined);
        onRemoveRight(selectedRightAccess, selectedUsers);
    }

    const disabledTrash = !selectedRightAccess;
    const disabledUsers = !selectedRightAccess;

    return (
        <div className={TW_ViewSharingEditorNewFilter}>
            <Select key={selectedRightAccess ?? "undefined"} onValueChange={onRightAccessSelectionChange} value={selectedRightAccess} disabled={selectedRightAccess !== undefined}>
                <SelectTrigger className={TW_ViewSharingEditorUserRightSelect}>
                    <SelectValue placeholder={t("view.sharing.right.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                    {viewRightAccesses.map((accessRight) => {
                        const disabled = usedRightAccess.includes(accessRight);
                        return (
                            <SelectItem key={accessRight} value={accessRight} disabled={disabled}>
                                {t(`right.access.value.view.${accessRight}`)}
                            </SelectItem>
                        )
                    })}
                </SelectContent>
            </Select>
            <div className="flex flex-row justify-start items-center gap-2 w-full">
                <UserSelect
                    allUsers={allUsers}
                    selectedUsers={users}
                    disabled={disabledUsers}
                    currentUserSelected={false}
                    onSelectionChange={onUserSelectionChange}
                    className={"w-full"}
                    allowCurrentUserSelection={false}
                />
            </div>
            {disabledTrash && <Trash2 className={TW_ViewSharingEditorRemoveFilterDisabled} />}
            {!disabledTrash && <Trash2 className={TW_ViewSharingEditorRemoveFilter} onClick={onRemoveRightAccess} {...trashHover} />}
        </div>
    )
}

export default ViewSharingEditor;
